<template>
  <div class="project-theme-selector">
    <Portal>
      <Modal :show="showThemeUpgradeWarning" class="project-mutate-theme__theme-upgrade-modal">
        <Card theme="flat" :title="$t('updateTemplateWarningTitle')">
          <template #default>
            <div v-html="$t('updateTemplateWarning')" />
          </template>
          <template #footer>
            <ButtonComponent :icon="['fal', 'times']" :label="$t('cancel')" @click="close" />
            <ButtonComponent :label="$t('upgradeAnyway')" @click="close" />
            <ButtonComponent theme="primary" :label="$t('chatWithSupport')" @click="chat" />
          </template>
        </Card>
      </Modal>
    </Portal>

    <Alert v-if="isCustomTheme" class="project-mutate-theme__custom" data-field="themes">{{
      $t('customThemeActiveMessage')
    }}</Alert>

    <div
      v-else
      v-auto-animate="{ disabled: isSlot }"
      :class="
        isSlot
          ? 'project-theme-selector__content'
          : ['field-renderer', 'field-renderer--collapse', { 'field-renderer--expanded': sectionExpanded }]
      "
      :style="isSlot ? null : { '--list-item-min-width': '100%' }"
    >
      <button
        v-if="!isSlot"
        class="field-renderer__collapsible-header"
        type="button"
        @click="sectionExpanded = !sectionExpanded"
      >
        <Icon :icon="['fal', 'angle-down']" size="lg" :rotation="sectionExpanded ? '180' : null" />
        <legend class="field-renderer__collapsible-labels">
          <div class="field-renderer__collapsible-labels-label">{{ $t('selectTheme') }}</div>
          <div class="field-renderer__collapsible-labels-sublabel">{{ $t('projectThemeHelper') }}</div>
        </legend>
      </button>

      <div v-if="isSlot || sectionExpanded" :class="{ 'field-renderer__item': !isSlot }">
        <div class="project-mutate-theme__items">
          <ThemeCard
            v-for="theme in themesComp"
            :key="'theme_' + theme.value"
            :value="theme"
            :active="theme.value === value"
            @select="$emit('input', theme.value)"
            :disabled="disabled"
          />
        </div>

        <div
          v-if="orginalThemeType === 'old' || $store.getters.isSuperadmin"
          class="project-mutate-theme__load-trigger"
        >
          <ButtonComponent
            theme="secondary"
            :label="expand ? $t('showLess') : $t('seeOldThemes')"
            @click="expand = !expand"
            :icon="['fal', `${expand ? 'minus' : 'plus'}`]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ThemeCard from '@/components/Project/ThemeCard.vue'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { ButtonComponent } from 'vue-elder-button'
import { ModalComponent as Modal } from 'vue-elder-modal'
import Portal from '@/components/Portal'
import Card from '@/components/Card'
import Bus from '@/bus'
import autoAnimate from '@formkit/auto-animate'

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    value: String,
    isSlot: Boolean,
    disabled: Boolean,
  },
  data() {
    return {
      expand: false,
      orginalThemeType: '',
      showThemeUpgradeWarning: false,
      sectionExpanded: true,
    }
  },
  watch: {
    'data.theme': {
      immediate: true,
      handler(newValue) {
        if (!newValue) return
        const storageKey = `project-has-old-theme-${this.data.id}`
        const storageValue = sessionStorage.getItem(storageKey)
        this.orginalThemeType = storageValue
        if (storageValue) return

        const theme = this.themes.find(i => i.value === newValue)
        if (!theme) return
        const saveValue = !['favorite'].includes((theme.tags || [{}])[0].id) ? 'old' : 'new'
        sessionStorage.setItem(storageKey, saveValue)
        this.orginalThemeType = saveValue
      },
    },
    currentTheme: {
      handler(newValue) {
        if (!newValue || this.showThemeUpgradeWarning) return
        const currentThemeIsFav = ['favorite'].includes((newValue.tags || [{}])[0].id)
        const savedThemeIsFav = ['favorite'].includes(
          ((this.themes.find(i => i.value === this.data.theme) || {}).tags || [{}])[0].id,
        )
        this.showThemeUpgradeWarning = this.orginalThemeType === 'old' && currentThemeIsFav && !savedThemeIsFav
      },
    },
  },
  computed: {
    ...mapState({
      themes(state) {
        return (state.enums.Themes || [])
          .filter(({ type }) => type === 'project')
          .sort((a, b) => {
            if ((a.tags || [{}])[0].id === 'favorite') return -1
            return 0
          })
          .filter(item => {
            return (item.tags || []).find(i => i.id === 'intern') ? this.$store.getters.isSuperadmin : true
          })
      },
    }),
    isCustomTheme() {
      if (!this.value) return false
      if (!this.themes.length) return false
      return !this.currentTheme
    },
    currentTheme() {
      return this.themes.find(t => t.value === this.value)
    },
    themesComp() {
      if (!this.expand) {
        const lastAndCurrentTheme = [this.$path('currentTheme.value', this), this.data.theme]
        return this.themes.filter(i => {
          return ['favorite'].includes((i.tags || [{}])[0].id) || lastAndCurrentTheme.includes(i.value)
        })
      }
      return this.themes
    },
  },
  methods: {
    close() {
      this.showThemeUpgradeWarning = false
    },
    chat() {
      Bus.$emit(
        'chat',
        `${this.$t('upsaleRequestTemplate', [
          this.$t('supportUpdateTemplate', {
            template: this.$path('currentTheme.label', this),
            project: this.data.name,
          }),
        ])}`,
      )
    },
  },
  directives: {
    autoAnimate: {
      inserted(el, binding) {
        if (binding.value && binding.value.disabled) return
        autoAnimate(el)
      },
    },
  },
  components: {
    ThemeCard,
    Alert,
    ButtonComponent,
    Modal,
    Portal,
    Card,
  },
}
</script>
